import React, { useState, useReducer } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CarLeadCard from './CarLeadCard';
import CarLeadRouting from './CarLeadRouting';
import ContactHistory from '../../pages/LeadsPage/ContactHistory';
import { StackedDialog } from '../../utilities/ModalStackContext';
import { CAR_SOLD_OTHER_BUYER } from '../../utilities/leadStatus';

export default function Leads(props) {
  const [selectedLead, setSelectedLead] = useState(null);
  // Cards mutate the lead object in place (status, rep, contacts) and call
  // onUpdate; bump a counter to re-render the tab with the new values.
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const { leads = [], filter = () => true } = props;
  const car = {
    stock: props.stock,
    year: props.year,
    make: props.make,
    model: props.model,
    trim: props.trim,
    slug: props.slug,
  };

  // Statuses that mean the lead is closed out. Unresponsive and duplicate are
  // treated as closed alongside the explicit dead/won/lost terminal states.
  // 'car sold - other buyer' is set by the backend when this car's deal funds.
  const CLOSED_STATUSES = ['dead', 'won', 'lost', 'unresponsive', 'duplicate', CAR_SOLD_OTHER_BUYER];

  // Newest lead first within each group.
  const byNewest = (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf();

  const rows = leads.filter(filter);
  const open = rows.filter(l => !CLOSED_STATUSES.includes(l.status)).sort(byNewest);
  const closed = rows.filter(l => CLOSED_STATUSES.includes(l.status)).sort(byNewest);

  const handleClose = () => setSelectedLead(null);
  const handleUpdate = () => {
    forceUpdate();
    if (props.onUpdate) props.onUpdate();
  };

  const renderGroup = (label, list, color) => (
    list.length > 0 && (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Chip label={list.length} size="small" color={color} variant="outlined" sx={{ height: 20 }} />
        </Box>
        <Stack spacing={1.5}>
          {list.map(lead => (
            <CarLeadCard
              key={lead.id}
              lead={lead}
              car={car}
              onUpdate={handleUpdate}
              onOpenDetail={setSelectedLead}
            />
          ))}
        </Stack>
      </Box>
    )
  );

  return (
    <>
      <CarLeadRouting car={props} />
      {rows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No leads for this car yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {renderGroup('Active', open, 'primary')}
          {renderGroup('Closed', closed, 'default')}
        </Stack>
      )}

      <StackedDialog open={Boolean(selectedLead)} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            m: 0, p: 2, display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider',
          }}
        >
          <span>Lead: {selectedLead?.name || 'No name'}</span>
          <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedLead && <ContactHistory lead={selectedLead} onUpdate={handleUpdate} />}
        </DialogContent>
      </StackedDialog>
    </>
  );
}
